/**
 * What a credit balance and a ledger row say about themselves, kept where a
 * render test can reach them.
 *
 * **They live apart from `CreditsPage` for the reason slice 13 split
 * `ReportCopy.tsx` and slice 14 split `AnalyticsCopy.tsx`.** That page imports
 * the API client, which imports `lib/config.ts`, which reads `import.meta.env`
 * and therefore only evaluates inside Vite — so anything left in it could be
 * rendered by nobody and asserted by nobody. Until slice 17 this screen had no
 * verify suite at all: the two claims below were held by reading, and reading is
 * what let "in a later slice" sit in a customer-facing sentence for four slices.
 * `scripts/verify-credits-screen.tsx` renders this file.
 *
 * **Three claims are made here, and each is a way this screen could lie while
 * every number on it stayed correct.**
 *
 * 1. **Reserved is not consumed, and a release is not a consumption.** `reserved`
 *    is held by messages still in flight; only a delivery ever writes
 *    `consume`. A bounce writes `release`, which moves `reserved` and leaves
 *    `balance` alone — and {@link LedgerAmountCell} shows that by reading
 *    `LEDGER_BALANCE_EFFECTS` through `ledgerBalanceMove` and
 *    `ledgerReservedMove`, never by re-deriving the arithmetic here. Two
 *    implementations of the same sum drift, and the way they drift is that a
 *    bounce starts reading as a charge.
 *
 * 2. **A tenant cannot top their own balance up, and no control here pretends
 *    otherwise.** Grants are an admin action. The rejected shape is an "Add
 *    credits" button that opens a dialogue explaining it is not available: a
 *    button that cannot work is worse than an absence, because a person presses
 *    it, is refused, and now distrusts the rest of the screen.
 *    {@link CreditsGrantNote} states the fact once, in one line, rather than
 *    leaving a hole where an affordance would be.
 *
 * 3. **A credit count is meaningless without the messages it buys.** "1,000
 *    credits" answers nothing; "about 1,000 more emails" answers the question
 *    the reader has. The conversion is `overviewChannelCredits` from the
 *    contract — the same function the Overview's tile uses — so this screen and
 *    that one cannot come to disagree about what a credit is worth, and neither
 *    of them hard-codes a ratio that the send path could move underneath them.
 *
 * **Never had a credit is not zero credits.** `updatedAt === null` means the
 * balance cache has never moved for this channel, and it gets a sentence rather
 * than a row of noughts — the same zero-versus-absent rule the analytics screen
 * carries for a channel a workspace does not send on. A workspace whose balance
 * genuinely reached zero keeps its zeros, because that is a real measurement and
 * the one a reader has to act on.
 *
 * **Nothing here says spend, cost, charge or price, and no figure carries a
 * currency symbol.** Under BYO the tenant pays AWS directly, in amounts Wend
 * never sees; a credit is Wend's metering unit and has no exchange rate. The
 * vocabulary rule is `credit.ts`'s and is enforced by the verify suite rather
 * than by anybody remembering it.
 *
 * **Slice 66 drew the three figures as panels and the filters as scope chips.**
 * Each channel's available, balance and reserved figures are the shared
 * `MetricPanel` at its smallest size, with **no meter**: a balance has no
 * maximum, so any bar under it would be a proportion of something invented.
 * Available keeps the violet panel because it is the one number a decision is
 * made on; the other two are the plain skin. The figures are sans with tabular
 * digits rather than mono, whose dotted zero reads as an 8 — and a credits
 * screen is where "0 available" must never be misread.
 *
 * The ledger's filter chips moved here from `CreditsPage` as
 * {@link LedgerFilterChips}, on the shared `ScopeChips`. Moving them is what let
 * the suite render them at all; before, the page's hand-rolled chips were held
 * by the compiler and nothing else. The chips are still one "All" plus one per
 * value, single-select, and the page still owns the filter state.
 */
import {
  CHANNEL_VALUES,
  overviewChannelCredits,
  type Channel,
  type CreditBalance,
  type CreditLedgerEntry,
  type LedgerEntryType,
} from '@wend/shared';
import { Kicker, MetricPanel, ScopeChips } from '@wend/ui';

import { CHANNEL_LABEL } from '../../lib/campaign-display';
import {
  LEDGER_ENTRY_DISPLAY,
  LEDGER_ENTRY_TYPE_ORDER,
  ledgerBalanceMove,
  ledgerEntrySentence,
  ledgerReservedMove,
} from '../../lib/credit-display';
import { formatCount, formatDateTime, pluralise } from '../../lib/format';

/**
 * What one credit buys, in the noun the reader recognises for that channel.
 *
 * Written out per channel rather than assembled from `CHANNEL_LABEL`, which
 * pluralises into "WhatsApps". `pluralise` supplies the plural, so the singular
 * is what is stored.
 */
const CHANNEL_MESSAGE_NOUN: Readonly<Record<Channel, string>> = {
  email: 'more email',
  whatsapp: 'more WhatsApp message',
};

/**
 * One figure, as a `MetricPanel` with no meter (see the header). The prop names
 * are unchanged so the call sites below read as they always did.
 */
function Figure({
  label,
  value,
  caption,
  emphasis = false,
}: {
  label: string;
  value: number;
  caption: string;
  /** The number a person should leave with. Exactly one per channel. */
  emphasis?: boolean;
}) {
  return (
    <MetricPanel
      kicker={label}
      figure={{ kind: 'measured', value }}
      figureSize="md"
      sub={caption}
      variant={emphasis ? 'brand' : 'plain'}
    />
  );
}

/**
 * One channel's three numbers, and what the available one is in messages.
 *
 * Available leads because it is the number a decision is made on — a campaign is
 * blocked on what is available, never on the balance — and it is the only one
 * with the message count under it, because the other two are not what anybody
 * can send.
 */
export function CreditsChannelBalance({ balance }: { balance: CreditBalance }) {
  const channel = CHANNEL_LABEL[balance.channel];
  const credits = overviewChannelCredits(balance.channel, balance);

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-5 first:border-t-0 first:pt-0">
      {/* The channel and its date are the kit's Kicker, as the admin panel
          draws them: the first cut had an ad-hoc 0.72rem / 0.14em mono label
          one line above the panels' 0.64rem / 0.13em kickers, two label
          scales a line apart (slice 66 review). */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Kicker as="span" tone="brand">
          {channel}
        </Kicker>
        {balance.updatedAt !== null && (
          <Kicker as="span">last moved {formatDateTime(balance.updatedAt)}</Kicker>
        )}
      </div>

      {balance.updatedAt === null ? (
        <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">
          No {channel} credit has ever reached this workspace — that is an absence, not a balance of
          zero.
        </p>
      ) : (
        <div className="grid items-stretch gap-3 sm:grid-cols-3">
          <Figure
            label="Available"
            value={credits.available}
            caption={`Balance minus reserved — about ${pluralise(
              credits.messagesRemaining,
              CHANNEL_MESSAGE_NOUN[balance.channel],
            )}, at ${pluralise(credits.creditsPerMessage, 'credit')} per message.`}
            emphasis
          />
          <Figure
            label="Balance"
            value={credits.balance}
            caption="Credits held. Only a delivery consumes one."
          />
          <Figure
            label="Reserved"
            value={credits.reserved}
            caption="Held by messages in flight. Accepted is not delivered, and neither is consumed."
          />
        </div>
      )}
    </div>
  );
}

/**
 * The one sentence that replaces the affordance this screen deliberately lacks.
 *
 * See claim 2 in the header. It is one line and it is visible, not a tooltip: a
 * person looking for a top-up button scans for a control and leaves, and the
 * whole cost of that is one sentence where they were already looking.
 */
export function CreditsGrantNote() {
  return (
    <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
      Credits are granted by Wend staff. There is no way to add them from this workspace.
    </p>
  );
}

/**
 * What one entry moved, read out of `LEDGER_BALANCE_EFFECTS` — never re-derived.
 *
 * A `reserve` and a `release` print against `reserved` with no balance line at
 * all, which is the whole point: a reader who bounced 400 messages sees 400
 * credits come back out of reserved and the balance untouched.
 */
export function LedgerAmountCell({ entry }: { entry: CreditLedgerEntry }) {
  const balanceMove = ledgerBalanceMove(entry);
  const reservedMove = ledgerReservedMove(entry);

  return (
    <div className="flex flex-col gap-0.5 font-mono text-[0.85rem] tabular-nums">
      {balanceMove !== null && (
        <span className={balanceMove.sign === '+' ? 'text-good' : 'text-bad'}>
          {balanceMove.sign}
          {formatCount(balanceMove.magnitude)}
          <span className="ml-1.5 font-sans text-[0.65rem] uppercase tracking-[0.08em] text-muted">
            balance
          </span>
        </span>
      )}
      {reservedMove !== null && (
        <span className="text-muted">
          {reservedMove.sign}
          {formatCount(reservedMove.magnitude)}
          <span className="ml-1.5 font-sans text-[0.65rem] uppercase tracking-[0.08em] text-muted">
            reserved
          </span>
        </span>
      )}
    </div>
  );
}

/** What happened, in the type's own words, plus whatever note was written on it. */
export function LedgerMeaningCell({ entry }: { entry: CreditLedgerEntry }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[0.85rem] leading-snug text-muted">{ledgerEntrySentence(entry)}</span>
      {entry.note !== null && (
        <span className="text-[0.78rem] leading-snug text-muted">{entry.note}</span>
      )}
    </div>
  );
}

/**
 * One "All" chip plus one per option, for the ledger's channel and type
 * filters — the shared `ScopeChips`, single-select.
 *
 * Generic over the value, so the channel filter and the entry-type filter share
 * one control without either re-declaring it. "All" is the `null` filter, and
 * it travels through the chips as the empty string: no channel and no ledger
 * type is ever spelled that way, so it cannot collide with a real value. The
 * value coming back is looked up in `options` rather than cast, so a chip value
 * the page did not offer can never reach the API as a filter.
 *
 * It lives here rather than in `CreditsPage` so that the suite can render it —
 * see the file header.
 */
export function LedgerFilterChips<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T | null) => void;
}) {
  return (
    <ScopeChips
      label={label}
      selection="single"
      value={value ?? ALL_CHIP}
      items={[{ value: ALL_CHIP, label: 'All' }, ...options]}
      onValueChange={(next) => {
        onChange(options.find((option) => option.value === next)?.value ?? null);
      }}
    />
  );
}

/** The "All" chip's value: the empty string, which no filter value is. */
const ALL_CHIP = '';

/**
 * The options the page's two filters offer, built here rather than inline in
 * `CreditsPage` so the suite renders the page's OWN options. The first slice 66
 * suite built its own list from `LEDGER_ENTRY_TYPE_ORDER` and claimed that a
 * filter which dropped a type would fail its count; it would not have — the
 * page could drop one and the suite's copy still had seven (review). Now the
 * suite renders these and types the labels it expects by hand.
 */
export function ledgerTypeOptions(): ReadonlyArray<{ value: LedgerEntryType; label: string }> {
  return LEDGER_ENTRY_TYPE_ORDER.map((type) => ({ value: type, label: LEDGER_ENTRY_DISPLAY[type].label }));
}

/** One option per channel; see {@link ledgerTypeOptions}. */
export function ledgerChannelOptions(): ReadonlyArray<{ value: Channel; label: string }> {
  return CHANNEL_VALUES.map((channel) => ({ value: channel, label: CHANNEL_LABEL[channel] }));
}
